/**
 * features/pos/store/cartPersistence.ts
 *
 * Canal lateral de persistencia para el carrito en curso y los tickets en
 * espera — NO usa el middleware `persist` de Zustand a propósito: esa opción
 * rehidrata en silencio, y aquí se necesita preguntar antes de recuperar
 * ("¿Recuperar venta pendiente?").
 *
 * La clave de localStorage incluye storeId + userId para que dos cajeros en
 * la misma tienda (o el mismo dispositivo compartido) nunca pisen ni
 * recuperen el draft del otro.
 */

import type { CartItem } from './cartStore'
import type { HeldTicket } from './heldStore'

export interface CartDraftSnapshot {
  savedAt: string
  cart: {
    items: CartItem[]
    clientId: string | null
    clientName: string | null
    clientBusinessName: string | null
    clientLevel: 'final' | 'pro' | 'vip'
    globalDiscount: number
    globalDiscountType: 'amount' | 'percent'
    quoteId: string | null
    quoteFolio: string | null
    quoteOriginStoreId: string | null
    quoteOriginStoreCode: string | null
    quoteOriginBusinessId: string | null
  }
  heldTickets: HeldTicket[]
}

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000

function draftKey(storeId: string, userId: string): string {
  return `celmaxx-pos-draft-v1:${storeId}:${userId}`
}

export function saveDraft(storeId: string, userId: string, snapshot: CartDraftSnapshot): void {
  try {
    localStorage.setItem(draftKey(storeId, userId), JSON.stringify(snapshot))
  } catch {
    // localStorage lleno o no disponible — el carrito sigue funcionando en memoria
  }
}

export function loadDraft(storeId: string, userId: string): CartDraftSnapshot | null {
  try {
    const raw = localStorage.getItem(draftKey(storeId, userId))
    if (!raw) return null
    const snapshot = JSON.parse(raw) as CartDraftSnapshot
    const age = Date.now() - new Date(snapshot.savedAt).getTime()
    if (!Number.isFinite(age) || age > DRAFT_TTL_MS) {
      clearDraft(storeId, userId)
      return null
    }
    return snapshot
  } catch {
    return null
  }
}

export function clearDraft(storeId: string, userId: string): void {
  try {
    localStorage.removeItem(draftKey(storeId, userId))
  } catch {
    // no-op
  }
}

export function isDraftEmpty(snapshot: CartDraftSnapshot | null): boolean {
  if (!snapshot) return true
  return snapshot.cart.items.length === 0 && snapshot.heldTickets.length === 0
}
